const R = require('ramda');
const utils = require('../utils/utils');
const metricUtils = require('../utils/metricUtils');
const { uniq } = require('../utils');

class ArchitectureElement {
  constructor({ architecture = null } = {}) {
    this.FEFormat = {};
    /** @type {AdAccountArchitecture} Ad Account Architecture this element is part of. */
    this.architecture = architecture;
    /** @type {'adAccount'|'adrielCampaign'|'project'|'campaign'|'adSet'|'ad'} Level of this element in the architecture. */
    //this.level
    /** @type {string} */
    this.subChannel;
    /** @type {string} Id of this element provided by the channel. */
    this.channelId;
    /** @type {string|undefined} Id of this element provided by adriel connect. */
    this.connectId;
    /** @type {string|undefined} Id of the campaign parent of this element (if any) provided by the channel. */
    this.channelCampaignId;
    /** @type {string|undefined} Id of the ad set parent of this element (if any) provided by the channel. */
    this.channelAdSetId;
    /** @type {string|undefined} Id of the ad parent of this element (if any) provided by the channel. */
    this.channelAdId;
  }

  get status() {
    return this.channelStatus;
  }

  get metric() {
    return this.getMetric();
  }

  getMetric({ report = undefined } = {}) {
    let metric = {};
    if (!report) {
      const reports = this.getReports();
      report = reports[0];
    }
    if (report && report.metric) {
      metric = metricUtils.getMetricFromReport(report, this);
    } else {
      return null;
    }
    if (this.level == 'campaign') {
      if (this.isLifeTimeBudget) {
        metric.lifeTimeSpend = metric.spend;
        metric.lifeTimeBudget = this.lifeTimeBudget;
      }
    }
    return metric;
  }

  /**
   *
   * @param {Object} [p]
   * @param {*} [p.dateGroup]
   * @param {*} [p.detail]
   * @returns
   */
  getConversionAndMetric({ dateGroup, detail } = {}) {
    const element = this;
    let reports;
    if (detail === 'subChannel') {
      if (this.isFacebook) {
        reports = this.getReports({ dateGroup, detail });
      } else {
        reports = this.getReports({ dateGroup });
        reports.forEach(report => (report.text = element.subChannels ? element.subChannels[0] : element.channel));
      }
    } else {
      reports = this.getReports({ dateGroup, detail });
    }
    if (!reports) return [];
    const conversionAndMetricList = reports.map(report =>
      metricUtils.getMetricAndConversionFromReport({ report, element }),
    );
    if (this.level === 'campaign') {
      if (this.isLifeTimeBudget) {
        conversionAndMetricList.forEach(row => {
          row.metric.lifeTimeSpend = row.metric.spend;
          row.metric.lifeTimeBudget = this.lifeTimeBudget;
        });
      }
    }
    if (conversionAndMetricList.length === 0) return null;
    if (!dateGroup && !detail) return conversionAndMetricList[0];
    return conversionAndMetricList;
  }

  get isArchitectureLevel() {
    return ['adAccount', 'campaign', 'adSet', 'ad'].includes(this.level);
  }

  get isFacebook() {
    if (this.isArchitectureLevel) {
      return this.adAccount._channel === 'facebook';
    } else return false;
  }

  get channel() {
    const adAccountChannel = this.adAccount?._channel || this._channel;
    if (this.level === 'adAccount') return adAccountChannel;
    if (adAccountChannel === 'externalFiles') {
      if (this.subChannels.length > 0) {
        return this.subChannels[0];
      } else {
        return null;
      }
    } else return adAccountChannel;
  }

  set channel(channel) {
    this._channel = channel;
  }

  get childrenIdsByLevel() {
    return this.architecture.childrenIdsByLevelByParentId[this.id];
  }

  getChildLevel() {
    let childLevel;
    if (this.level === 'adAccount') {
      childLevel = 'campaign';
    } else if (this.level === 'campaign') {
      childLevel = 'adSet';
    } else if (this.level === 'adSet') {
      childLevel = 'ad';
    }
    return childLevel;
  }

  getChildElementIds() {
    const childLevel = this.getChildLevel();
    if (!this.childrenIdsByLevel || !this.childrenIdsByLevel[childLevel]) return [];
    return this.childrenIdsByLevel[childLevel];
  }

  getKPI() {
    return this.architecture.getKPI({ id: this.id, level: this.level });
  }

  /**
   * @return {Project}
   */
  get project() {
    if (this.level === 'project') return this;
    if (this.campaign) {
      const projectId = this.architecture.parentIdByLevelByChildId[this.campaign.id]['project'];
      if (projectId) {
        return this.architecture.projectsById[projectId];
      }
    }
    return null;
  }

  get projectId() {
    const project = this.project;
    if (project) {
      return project.id;
    } else return null;
  }

  /**
   * get parent id depending of the level
   * @param {string} level can be project, campaign, adSet, etc...
   * @return {string|null} parentId
   */
  getParentElementId(level) {
    const parentIdByLevel = this.architecture.parentIdByLevelByChildId[this.id];
    if (parentIdByLevel) {
      return parentIdByLevel[level];
    } else return null;
  }

  /**
   * get parent element with the level
   * @param {string} level
   * @returns {ArchitectureElement} element
   */
  getParentElement(level) {
    const parentId = this.getParentElementId(level);
    const elementsById = this.architecture.elementsByIdByLevel[level];
    if (elementsById && parentId) {
      return elementsById[parentId];
    } else return null;
  }

  /**
   * @return {AdAccount}
   */
  get adAccount() {
    if (this.level === 'adAccount') return this;
    return this.getParentElement('adAccount');
    // const adAccountId = getParentId('adAccount');
    // if (adAccountId) {
    //     return this.architecture.adAccountsById[adAccountId];
    // }
    // return null;
  }

  /**
   * @return {AdrielCampaign}
   */
  get adrielCampaign() {
    const campaign = this.campaign;
    if (!campaign) return null;
    const adrielCampaignId = this.architecture.dimensionElementIdByDimensionIdByCampaignId[campaign.id]?.adrielCampaign;
    if (!adrielCampaignId) return null;
    return this.architecture.adrielCampaignsById[adrielCampaignId];
  }

  /**
   * @return {Campaign}
   */
  get campaign() {
    if (['project', 'adAccount'].includes(this.level)) return null;
    if (this.level === 'campaign') return this;
    if (this.level === 'ad' && this.adSet) return this.adSet.campaign;
    if (this.level === 'keyword' && this.adSet) return this.adSet.campaign;
    return this.getParentElement('campaign');
    // if (campaignId) {
    //     return this.architecture.campaignsById[campaignId];
    // }
    // return null;
  }

  /**
   * @return {[Campaign]}
   */
  get campaigns() {
    const childrenIdsByLevel = this.architecture.childrenIdsByLevelByParentId[this.id];
    if (!childrenIdsByLevel) return [];
    const campaignIds = childrenIdsByLevel['campaign'];
    if (campaignIds && campaignIds.length > 0) {
      return campaignIds.map(campaignId => this.architecture.campaignsById[campaignId]);
    } else return [];
  }

  /**
   * @return {AdSet}
   */
  get adSet() {
    if (this.level === 'adSet') return this;
    return this.getParentElement('adSet');
  }

  /**
   * @return {[AdSet]}
   */
  get adSets() {
    const childrenIdsByLevel = this.architecture.childrenIdsByLevelByParentId[this.id];
    if (!childrenIdsByLevel) return [];
    const adSetIds = childrenIdsByLevel['adSet'];
    if (adSetIds && adSetIds.length > 0) {
      return adSetIds.map(adSetId => this.architecture.adSetsById[adSetId]);
    } else return [];
  }

  /**
   * @return {[Ad]}
   */
  get ads() {
    if (this.level === 'campaign' && this.adSets && this.adSets.length > 0) {
      return this.adSets.flatMap(adSet => adSet.ads);
    }
    const childrenIdsByLevel = this.architecture.childrenIdsByLevelByParentId[this.id];
    if (!childrenIdsByLevel) return [];
    const adIds = childrenIdsByLevel['ad'];
    if (adIds && adIds.length > 0) {
      return adIds.map(adId => this.architecture.adsById[adId]);
    } else return [];
  }

  /**
   * @return {Boolean}
   */
  get isAdrielLeadForm() {
    return this.adrielCampaign ? this.adrielCampaign.isLeadForm : false;
  }

  /**
   * @returns {[string]}
   */
  get pixelEvents() {
    return this.architecture.pixelEventsByAdAccountId[this.adAccount.channelId];
  }

  /**
   * @returns {{type: String, value: Number}|null}
   */
  get formattedBudget() {
    if (!this.budget) return;
    const type = this.budget.type || this.budget.isTotalSum ? 'lifeTime' : 'daily';
    return {
      type,
      value: this.budget.amount,
    };
  }

  /**
   * @returns {Number|null}
   */
  get lifeTimeBudget() {
    if (!this.budget) return null;
    if (this.budget.isTotalSum) {
      return this.budget.amount;
    } else return null;
  }

  /**
   * @returns {Boolean}
   */
  get isLifeTimeBudget() {
    if (this.adrielCampaign) return this.adrielCampaign.isLifeTimeBudget;
    if (!this.budget) return null;
    return this.budget.isTotalSum;
  }

  get hasMultipleChannels() {
    return this.subChannels.length > 1;
  }

  get subChannelReports() {
    // for facebook we need to check the subChannels details
    let subChannelReports = [];
    if (this.isFacebook) {
      let subChannelDetails = this.getReports({
        detail: 'subChannel',
      });
      if (subChannelDetails && subChannelDetails.length > 0) {
        subChannelReports = subChannelDetails.map(detail => {
          const { metric, actions, from, to } = detail;
          return {
            subChannel: detail.text,
            metric,
            actions,
            from,
            to,
          };
        });
      }
    } else {
      let reports = this.reports || [];
      subChannelReports = reports.map(report => {
        const { metric, actions, from, to } = report;
        return {
          subChannel: this.subChannels[0],
          metric,
          actions,
          from,
          to,
        };
      });
    }
    subChannelReports = subChannelReports.map(report => ({
      ...report,
      metric: metricUtils.getMetricFromReport(report, this),
    }));
    // fix for lead missing maybe an issue with the pixel event or our form system
    if (this.campaign.isAdrielLeadForm && subChannelReports.length > 0) {
      // check if the nb of leads in the subchannels match the nb of leads in the ad
      const nbConversions = R.compose(R.sum, R.map(R.path(['metric', 'conversions'])))(subChannelReports);
      const nbLeads = R.compose(R.sum, R.map(R.path(['metric', 'leads'])))(this.reports);
      // if we have more leads in the db we should add up in the subchannel
      let totalDiff = nbLeads - nbConversions;
      if (totalDiff > 0) {
        subChannelReports[0].metric.conversions += totalDiff;
      } else if (totalDiff < 0) {
        totalDiff = Math.abs(totalDiff);
        for (const subChannelReport of subChannelReports) {
          const conversions = R.path(['metric', 'conversions'], subChannelReport);
          let nbConversionToRemove = Math.min(conversions, totalDiff);
          subChannelReport.metric.conversions -= nbConversionToRemove;
          totalDiff -= nbConversionToRemove;
        }
      }
    }
    return subChannelReports;
  }

  getSubChannelConversions() {
    let subChannelDetails = this.getReports({ detail: 'subChannel' });
    if (this.isFacebook && this.hasMultipleChannels && subChannelDetails && subChannelDetails.length > 0) {
      return subChannelDetails.map(subChannelDetail => {
        return {
          subChannel: subChannelDetail.text,
          conversionsByTracker: utils.getConversionsByTracker(subChannelDetail.actions),
        };
      });
    } else if (!(this.isFacebook && this.hasMultipleChannels) && this.reports && !R.isEmpty(this.reports)) {
      return [
        {
          subChannel: this.subChannels[0],
          conversionsByTracker: this.getConversionsByTracker(),
        },
      ];
    } else if (this.children) {
      const adsConversionsByTrackers = this.children
        .map(child => child.getSubChannelConversions())
        .flat()
        .filter(Boolean);
      return utils.mergeSubChannelConversions(adsConversionsByTrackers);
    } else return [];
  }

  /**
   * group all conversion by type of tracker
   * search children
   * @returns {{ga:[], pixel:[], lead:[]}}
   */
  getConversionsByTracker({ report } = {}) {
    let reports = report ? [report] : this.getReports();
    if (reports && !R.isEmpty(reports)) {
      let report = reports[0];
      return utils.getConversionsByTracker(report.actions);
    } else if (!report && this.children) {
      const adsConversionsByTrackers = this.children.map(child => child.getConversionsByTracker()).filter(Boolean);
      return utils.mergeConversionsByTracker(adsConversionsByTrackers);
    } else {
      return [];
    }
  }

  getBudget({ computedInformation }) {
    let budget = null;
    if (this.level === 'adAccount') {
      budget = computedInformation?.[this.level]?.budget;
      if (budget?.type) {
        budget = {
          lifeTime: 0,
          daily: 0,
          ...{
            [budget.type]: budget.value,
          },
        };
      }
    } else {
      if (this.formattedBudget) {
        budget = {
          lifeTime: 0,
          daily: 0,
          ...{
            [this.formattedBudget.type]: this.formattedBudget.value,
          },
        };
      } else if (computedInformation?.[this.level]?.[this.channelId]?.budget) {
        budget = computedInformation?.[this.level]?.[this.channelId]?.budget;
        if (budget?.type) {
          budget = {
            lifeTime: 0,
            daily: 0,
            ...{
              [budget.type]: budget.value,
            },
          };
        }
      }
    }
    return budget;
  }

  get subChannelMetrics() {
    let subChannelReports = this.subChannelReports || [];
    const subChannels = R.compose(
      R.filter(Boolean),
      R.map(([subChannel, data]) => {
        if (!data) return null;
        const { metric, actions } = utils.mergeReportArray(data);
        return {
          subChannel,
          metric,
          actions,
        };
      }),
      Object.entries,
      utils.groupBySubChannel,
      R.flatten,
      R.filter(Boolean),
    )(subChannelReports);

    return subChannels;
  }

  getSubChannelData() {
    const conversionsPerSubChannel = this.getSubChannelConversions();
    const metricsPerSubChannel = this.subChannelMetrics;

    const _map = R.indexBy(R.prop('subChannel'), conversionsPerSubChannel);

    return metricsPerSubChannel.map(subChannelData => ({
      ...subChannelData,
      ...(_map[subChannelData.subChannel] || {}),
    }));
  }

  get subChannelReportsByDate() {
    let subChannelReports = [];
    // for facebook we need to check the subChannels details
    if (this.isFacebook && this.hasMultipleChannels) {
      let subChannelDetailsGroupByDate = this.getReports({
        detail: 'subChannel',
        dateGroup: this.architecture.filter.dateGroup,
      }); // this.subChannelDetailsGroupByDate || []
      if (subChannelDetailsGroupByDate && subChannelDetailsGroupByDate.length > 0) {
        subChannelReports = subChannelDetailsGroupByDate.map(detail => {
          const { metric, actions, from, to } = detail;
          return {
            subChannel: detail.text,
            metric,
            actions,
            from,
            to,
          };
        });
      }
    } else {
      let reports = this.getReports({ dateGroup: 'day' });
      subChannelReports = reports.map(report => {
        const { metric, actions, from, to } = report;
        return {
          subChannel: this.subChannels[0],
          metric,
          actions,
          from,
          to,
        };
      });
    }
    subChannelReports = subChannelReports.map(report => ({
      ...report,
      metric: metricUtils.getMetricFromReport(report, this),
    }));
    return subChannelReports;
  }

  // Get conversions and metrics for excel reports
  // TODO: this function could be much shorter and maybe merged in getReports with a format option
  getConversionsAndMetrics(channel) {
    const conversions = this.getConversionsByTrackerPerDate({ channel, dateGroup: this.architecture.filter.dateGroup });
    // let metrics = this.reportsPerDate;
    // if(channel && ['facebook','instagram'].includes(channel)){
    let metrics = this.subChannelReportsByDate;
    // }
    if (channel) metrics = metrics.filter(data => data.subChannel === channel);

    const mergedByConversionsMetrics = R.groupBy(data => data.from + ':' + data.to)(conversions.concat(metrics));

    return Object.entries(mergedByConversionsMetrics).map(([dateFactor, mergedArr]) => {
      const from = mergedArr[0].from;
      const to = mergedArr[0].to;
      return mergedArr.reduce((data, curr) => {
        const { conversionsByTracker, metric } = curr; // property for need to merge
        // from conversionByTrackerPerDate
        if (conversionsByTracker) {
          data = {
            ...data,
            conversionsByTracker,
          };
        }
        // from reportsPerDate
        if (metric) {
          data = {
            ...data,
            metric,
          };
        }
        return data;
      });
    });
  }

  getConversionsByTrackerPerDate({ channel, dateGroup } = {}) {
    // let reports = this.reports;
    let reports = this.getReports({ dateGroup });
    // if(channel && ['instagram','facebook'].includes(channel)){
    // const reports = this.subChannelReportsByDate || this.reports;
    // if (
    //     this.subChannelReportsByDate &&
    //     this.subChannelReportsByDate.length > 0
    // )
    //     reports = this.subChannelReportsByDate;
    if (!reports) return [];
    // }
    const filteredReports = channel ? reports.filter(data => data.subChannel === channel).filter(Boolean) : reports;
    return filteredReports.map(report => {
      const { from, to, actions } = report;
      const conversionsByTracker = getConversionsByTracker(actions);
      return {
        from,
        to,
        conversionsByTracker,
      };
    });
  }

  // getConversionAndMetrics(channel) {
  //   return this.getConversionsAndMetrics(channel)
  // }
  getConversionAndMetrics(channel) {
    const conversions = this.getConversionsSummaryByTrackerPerDate(channel);
    let metrics = this.getSubChannelReportsPerDate(channel);
    // }

    const mergedByConversionsMetrics = R.groupBy(data => data.from + ':' + data.to)(conversions.concat(metrics));
    const { isConversion, isRemarketing, isLookalike } = this;
    return Object.entries(mergedByConversionsMetrics).map(([dateFactor, mergedArr]) => {
      const from = mergedArr[0].from;
      const to = mergedArr[0].to;
      return mergedArr.reduce((data, curr) => {
        const { conversionsByTracker, metric } = curr; // property for need to merge
        // from conversionByTrackerPerDate
        if (conversionsByTracker) {
          data = {
            ...data,
            conversionsByTracker,
          };
        }
        // from reportsPerDate
        if (metric) {
          data = {
            ...data,
            metric,
          };
        }
        return {
          ...data,
          isConversion,
          isLookalike,
          isRemarketing,
        };
      });
    });
  }

  getSubChannelReportsPerDate(channel) {
    let subChannelReportsByDate = this.getReports({
      detail: 'subChannel',
      dateGroup: this.architecture.filter.dateGroup,
    });
    const reports = subChannelReportsByDate
      .filter(report => !this.isFacebook || report.text === channel)
      .filter(Boolean);
    return reports;
    // no need to merge anymore
    // const mergedReports = mergeReportArrayByDate(reports);
    // return mergedReports;
  }

  getGAReports(dateGroup) {
    let GAReports = [];
    let GAReportsById = this.architecture.GAReportsById;
    if (dateGroup) {
      GAReportsById = this.architecture.GAReportsByIdByDateGroup[dateGroup];
    }
    if (GAReportsById) {
      GAReports = GAReportsById[this.id] || [];
    }
    // We don't have the GA reports on the adAccount level so we get them from the campaigns
    if (this.level === 'adAccount') {
      if (this.childrenIdsByLevel) {
        const campaignIds = this.childrenIdsByLevel['campaign'];
        GAReports = campaignIds
          .flatMap(campaignId => {
            return GAReportsById[campaignId];
          })
          .filter(Boolean);
      }
    }
    return GAReports;
  }

  /**
   * central access point to read any type of reports
   * @param {Object} opts
   * @param {"day"|"week"|"month"|null} opts.dateGroup get reports for every date with in a range
   * @param {"subChannel"|"keyword"|null} opts.detail get reports for each subChannels, keywords, ...
   * @returns {[{metric:object, actions:[object], campaignId?:string, adSetId?:string, adId:string, from?:string, to?:string, subChannel?:string, keyword?:string}]}
   */
  getReports({ dateGroup = null, detail = null } = {}) {
    if (
      detail &&
      !['subChannel', 'newAndReturning', 'age', 'gender', 'location', 'searchKeywords', 'deviceTarget'].includes(detail)
    ) {
      console.warn(`Unknown detail: ${detail}`);
    }
    if (detail == 'subChannel' && !this.isFacebook) detail = undefined;
    let reportsById;
    if (detail) {
      let reportsByIdByDetail = this.architecture.reportsByIdByDetail;
      if (dateGroup) {
        reportsByIdByDetail = this.architecture.reportsByIdByDetailByDateGroup[dateGroup];
      }
      reportsById = reportsByIdByDetail[detail];
    } else {
      if (dateGroup) {
        reportsById = this.architecture.reportsByIdByDateGroup[dateGroup];
      } else reportsById = this.architecture.reportsById;
    }
    if (!reportsById) return [];
    let reports = reportsById[this.id] || [];
    // merge with GA report
    if (!detail || detail == 'subChannel') {
      const GAReports = this.getGAReports(dateGroup);
      reports = reports.map(report => {
        let GAReportsFiltered = [...GAReports];
        // find matching GA reports with same date
        if (dateGroup) {
          GAReportsFiltered = GAReportsFiltered.filter(GAReport => GAReport.from == report.from);
        }
        // if detail is subChannel, filter only GA reports about this subChannel
        if (detail == 'subChannel') {
          GAReportsFiltered = GAReportsFiltered.filter(
            GAReport => GAReport.subChannels && GAReport.subChannels.includes(report.text),
          );
        }
        if (GAReportsFiltered.length == 0) return report;
        const GAActions = GAReportsFiltered.map(r => r.actions).flat();
        const GAMetric = GAReportsFiltered.map(r => r.metric).reduce((acc, metric) => utils.sumObject(acc, metric), {});
        Object.assign(report.metric, GAMetric);
        // merge all actions together
        const actions = utils.mergeActions([...GAActions, ...(report.actions || [])]);
        for (let action of actions) {
          Object.assign(action, {
            spend: report.metric.spend,
            clicks: report.metric.clicks,
            spendOnActions: report.metric.spend, // duplicated for the excel until we refactor to use the same variable
            clicksOnActions: report.metric.clicks,
          });
        }
        return {
          ...report,
          actions,
        };
      });
    }
    if (reports.length === 0 && !dateGroup && !detail) {
      reports.push({
        metric: {},
      });
    }
    return reports;
  }

  // deprecated shorthands for back-compatibility - start
  get subChannelDetailsGroupByDate() {
    console.log('Deprecated call, rather use getReports.');
    return this.getReports({ detail: 'subChannel', perDate: true });
  }
  get subChannelDetails() {
    console.log('Deprecated call, rather use getReports.');
    return this.getReports({ detail: 'subChannel' });
  }
  get age() {
    console.log('Deprecated call, rather use getReports.');
    return this.getReports({ detail: 'age' });
  }
  // deprecated - end

  getConversionGoalMap() {
    let subChannelDetailsGroupByDate = this.subChannelDetailsGroupByDate;
    if (!subChannelDetailsGroupByDate || R.isEmpty(subChannelDetailsGroupByDate)) {
      if (this.children) {
        const conversionGoalMaps = this.children.map(child => child.getConversionGoalMap());
        return utils.unionNameToKeyMap(conversionGoalMaps);
      } else return {};
    }
    const allKeyandName = new Set([
      ...subChannelDetailsGroupByDate
        .map(report => report.actions)
        .flat()
        .filter(Boolean)
        .map(action => `${action.key}:${action.name}`),
    ]);
    const nameToKey = {};
    for (let combinedKey of allKeyandName) {
      const [key, name] = combinedKey.split(':');
      if (key !== 'undefined' && key !== 'null' && name !== 'undefined' && name !== 'null') {
        nameToKey[key] = name;
      }
    }
    return nameToKey;
  }

  getConversionGoal() {
    if (this.conversionGoal) return this.conversionGoal;
    if (this.campaign.objective === 'APP_INSTALLS') return 'app_install';
    let conversionGoals = uniq(this.campaign.adSets.map(adSet => adSet.conversionGoal));
    if (conversionGoals.length > 1) return 'multiple';
    else if (conversionGoals.length == 1) return conversionGoals[0];
    else return null;
  }

  get reports() {
    // return []
    const reportsById = this.architecture.reportsById;
    let reports = reportsById[this.id] || [];

    // if (reports) {
    //     reports = reports.filter(
    //         (report) =>
    //             report.metric &&
    //             (!report.from || new Date(report.from) < new Date())
    //     );
    // } else reports = [];

    // add leads
    if (this.campaign && this.campaign.isAdrielLeadForm && !R.isEmpty(reports)) {
      // add leads to report data
      const leads = this.leads || [];
      // reports = reports.map((report) => {
      //     const metric = { leads: 0, ...report.metric };
      //     let lead;
      //     if (this.reports.length === 1) {
      //         lead = leads.length > 0 ? this.leads[0] : null;
      //     } else {
      //         lead = leads.find(R.propEq('from', report.from));
      //     }
      //     metric.leads = lead ? lead.value : 0;
      //     if (!report.actions) report.actions = [];
      //     report.actions.push({
      //         type: 'lead',
      //         name: 'lead',
      //         value: metric.leads,
      //     });
      //     return { ...report, metric };
      // });
    }

    // if (reports.length === 1) {
    //   element.report = element.reports[0];
    // }
    return reports;
  }
  get report() {
    let reports = this.reports;
    if (reports.length === 1) {
      return this.reports[0];
    }
    return null;
  }
  get leads() {
    let element = this;
    let level = this.level;
    let leadsByConnectAdId = this.architecture.leadsById;
    if (leadsByConnectAdId && Object.keys(leadsByConnectAdId).length > 0 && element.connectId) {
      if (level == 'ad') {
        return leadsByConnectAdId[element.connectId] || [];
      } else {
        let adsLeads = element.ads.map(ad => leadsByConnectAdId[ad.connectId] || []).flat();
        return Object.values(R.groupBy(adsLead => adsLead.from + '~' + adsLead.to, adsLeads)).map(leads => ({
          from: leads[0].from,
          to: leads[0].to,
          connectAdId: leads[0].connectAdId,
          value: R.sum(leads.map(lead => lead.value)),
        }));
      }
    } else return [];
  }

  hasDisapprovedAds() {
    return this.ads.some(ad => ad.status === 'DISAPPROVED');
  }

  getEffectiveStatus() {
    if (this.level === 'campaign') {
      if (this.status === 'RUNNING') {
        if (this.adSets.every(adSet => adSet.status !== 'RUNNING')) {
          return 'PAUSED';
        } else if (this.ads.every(ad => ad.status !== 'RUNNING')) {
          return 'PAUSED';
        }
      }
    }
    return this.status;
  }

  get objectives() {
    // if apple's objective is not fixed on connect we need to uncomment this
    if (this.channel === 'apple') return ['APP_INSTALLS'];
    return [this?.campaign?.objective];
  }

  format({
    includeChildren = false,
    dateGroup = null,
    parentElementTableId = null,
    report = null,
    metricList = null,
    computedInformation,
  } = {}) {
    const obj = {
      ...R.pick(['id', 'channelId', 'name', 'level', 'active', 'schedule', 'platformLink'], this),
      // conversionsByTracker: this.getConversionsByTracker({ report }),
      // kpi: this.getKPI(),
    };

    // for the graph we need the metric per date
    if (dateGroup) {
      obj.conversionsAndMetricPerDate = this.getConversionAndMetric({
        dateGroup,
      });
    } else {
      const metric = metricUtils.getComputedMetric({
        metric: this.getMetric({ report }),
        metricList: metricList || this.architecture.availableMetrics,
        conversionsByTracker: this.getConversionsByTracker({ report }),
      });

      obj.cell = {
        ...metric,
        ...R.pick(
          ['objectives', 'subChannels', 'status', 'bidCap', 'bidStrategies', 'placements', 'channelSubType'],
          this,
        ),
        budget: this.getBudget({ computedInformation }),
        adAccountName: this.adAccount?.name,
        campaignName: this.campaign?.name,
        campaignId: this.campaign?.channelId,
        adSetName: this.adSet?.name,
        adSetId: this.adSet?.channelId,
        adName: this.level === 'ad' ? this.name : null,
        adId: this.level === 'ad' ? this.channelId : null,
        level: this.level,
      };

      obj.channelAdAccountId = this.adAccount.channelId;
      obj.channelCampaignId = this.campaign?.channelId;
      obj.channelAdSetId = this.adSet?.channelId;
      obj.channelAdId = this.level === 'ad' ? this.channelId : null;
      obj.channelKeywordId = this.level === 'keyword' ? this.channelId : null;

      if (this.level === 'keyword') {
        obj.cell.matchingType = this.type;
      }
      if (!obj.cell.subChannels) obj.cell.subChannels = [this.channel];

      if (obj.cell.objectives) {
        obj.cell.objectives = obj.cell.objectives.filter(x => x);
        if (obj.cell.objectives.length == 0 && computedInformation?.adAccount) {
          if (computedInformation?.adAccount?.objectiveList?.length > 1) {
            obj.cell.objectives = ['MULTI_OBJECTIVES'];
          } else {
            obj.cell.objectives = computedInformation?.adAccount?.objectiveList || [];
          }
        }
      }

      //this.adSet.schedule is to add Starts and Ends in Ad or other row
      const schedule =
        this.schedule ||
        this.adSet?.schedule ||
        this.campaign?.schedule ||
        computedInformation?.[this?.level]?.[this?.channelId]?.schedule;
      if (schedule) {
        obj.cell.starts = schedule.startDate;
        if (!schedule.continuously) {
          obj.cell.ends = schedule.endDate;
        }
      }
    }
    if (includeChildren && this.getChildLevel() != null) {
      obj.children = this.children.map(child => child.format({ includeChildren, parentElementTableId }));
    }
    obj.tableId = obj.id;
    if (parentElementTableId) {
      obj.tableId += '-' + parentElementTableId;
    }
    if (report) {
      obj.from = report.from;
      obj.to = report.to;
    }
    return obj;
  }

  toJSON() {
    return { ...this, architecture: undefined };
  }
}

module.exports = ArchitectureElement;
